import ObjectID from 'bson-objectid';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { createContext, useState } from 'react';
import {
  fetchMembersForBillingReports,
  fetchMembersWithOptions,
  getMemberById,
  createMember,
  updateMember,
  selectDerivedFields,
  processReadings,
  applyClinicalRules,
} from '../services/MembersApi';
import {
  getDevicesByMemberId,
  orderBodyTraceDevice,
  connectExistingDevice,
} from '../services/RegistryApi';
import { sendSMS } from '../services/SMSApi';
import {
  connectTenoviDevice,
  orderTenoviDevice,
  buildTenoviDevices,
} from '../services/TenoviApi';
import {
  getYM,
  processMemberData,
  setCurrBillingInsights,
} from './Common/analytics';
import {
  activeMeds,
  activeMemos,
  parseNote,
} from './Common/memberUtils';
import { daysTillEvent } from './Common/TodoUtils';
import { platformServices, senderTypes } from './Common/Values';
import { useStitchAuth } from './StitchAuth';

// Create a React Context that lets us expose and access auth state
// without passing props through many levels of the component tree
const StitchMembersContext = createContext();

// Create a React Hook that lets us get data from our auth context
export function useStitchMembers() {
  const context = React.useContext(StitchMembersContext);
  if (!context) {
    throw new Error(
      `useStitchMembers must be used within a StitchMembersProvider`,
    );
  }
  return context;
}

export function StitchMembersProvider(props) {
  const { userCustomData } = useStitchAuth();
  const [memberAll, setMemberAll] = useState();
  const [nTot, setNTot] = useState(0);
  const nLimit = 50;
  const [memberOne, setMemberOne] = useState();
  const [opInsights, setOpInsights] = useState();

  // We useMemo to improve performance by eliminating some re-renders
  const memberInfo = React.useMemo(() => {
    const reset = () => {
      setMemberAll(null);
      setMemberOne(null);
      setOpInsights(null);
    };

    const buildMemberDevices = async (memberId) => {
      // the db returns device info in array
      const deviceRegistryArr = await getDevicesByMemberId(memberId);
      let deviceRegistry = {};
      let devices = [];
      if (deviceRegistryArr.length > 0) {
        // break down the array into an object where the keys are the device
        deviceRegistry = _.keyBy(deviceRegistryArr, 'device');
        // get array of just the devices
        devices = deviceRegistryArr.map(
          (memberDevice) => memberDevice['device'],
        );
      }
      return { deviceRegistry, devices };
    };

    const loadMember = async (memberId) => {
      const fetchMemberBaseData = async () => {
        let memberData = await getMemberById(memberId);
        if (memberData) {
          memberData._id = ObjectID(memberData._id).toHexString();
        }
        return memberData;
      };
      try {
        if (memberId) {
          const deviceInfo = await buildMemberDevices(memberId);
          const [memberData, tenoviDevices] = await Promise.all([
            fetchMemberBaseData(),
            buildTenoviDevices(memberId),
          ]);
          memberData.devices = deviceInfo.devices;
          memberData.deviceRegistry = deviceInfo.deviceRegistry;
          memberData.tenoviDevicesStrings =
            memberData?.tenoviDevices || [];
          memberData.tenoviDevices = tenoviDevices;
          const memberDataFinal = setCurrBillingInsights(
            memberData,
            userCustomData.timeZone,
          );
          setMemberOne(memberDataFinal);
        }
      } catch (e) {
        console.log(e);
      }
    };

    const loadMembers = async (
      query = { disenrolled: false },
      sortOption = { dataReceivedDT: -1 },
      pageNum = 0,
    ) => {
      const projection = {
        disenrolled: 1,
        disenrolledCcm: 1,
        name: 1,
        phone: 1,
        dob: 1,
        gender: 1,
        enrollmentDate: 1,
        lastActiveDate: 1,
        deviceReceiptDate: 1,
        bodyWeight: 1,
        bodyWeightLastDT: 1,
        bodyWeightDiff: 1,
        systolicBP: 1,
        systolicBPLastDT: 1,
        systolicBPDiff: 1,
        diastolicBP: 1,
        diastolicBPDiff: 1,
        pulse: 1,
        pulseDiff: 1,
        pulseLastDT: 1,
        ecg: 1,
        ecgLastDT: 1,
        bloodSugar: 1,
        bloodSugarLastDT: 1,
        bloodSugarDiff: 1,
        step: 1,
        stepLastDT: 1,
        stepDiff: 1,
        spo2: 1,
        spo2LastDT: 1,
        spo2Diff: 1,
        pef: 1,
        pefLastDT: 1,
        pefDiff: 1,
        cpts: 1,
        activeMemos: 1,
        measureFreq: 1,
        warningLog: 1,
        numUnreadMessages: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
        dataReceivedDT: 1,
        diagnosisCodes: 1,
        practiceId: 1,
        missedCall: 1,
        devices: 1,
        kardiaId: 1,
        timeSpent: 1,
        optInSMS: 1,
        email: 1,
        events: 1,
        liveBilling: 1,
        services: 1,
        careplans: 1,
        memberTitle: 1,
      };
      const options = {
        query,
        projection,
        sortOption,
        limit: nLimit,
        skip: nLimit * pageNum,
      };
      let memberDataDoc = await fetchMembersWithOptions(options);

      let memberData = memberDataDoc.data;
      let nTot = memberDataDoc.nTot;

      for (let i = 0; i < memberData.length; i++) {
        memberData[i] = setCurrBillingInsights(
          memberData[i],
          userCustomData.timeZone,
        );
      }

      setMemberAll(memberData);
      setNTot(nTot);
      return memberData;
    };

    const loadMembersForServiceTabs = async (
      query = { disenrolled: false },
      sortOption = { dataReceivedDT: -1 },
    ) => {
      const projection = {
        disenrolled: 1,
        disenrolledCcm: 1,
        name: 1,
        phone: 1,
        dob: 1,
        gender: 1,
        enrollmentDate: 1,
        lastActiveDate: 1,
        deviceReceiptDate: 1,
        bodyWeight: 1,
        bodyWeightLastDT: 1,
        bodyWeightDiff: 1,
        systolicBP: 1,
        systolicBPLastDT: 1,
        systolicBPDiff: 1,
        diastolicBP: 1,
        diastolicBPDiff: 1,
        pulse: 1,
        pulseDiff: 1,
        pulseLastDT: 1,
        ecg: 1,
        ecgLastDT: 1,
        bloodSugar: 1,
        bloodSugarLastDT: 1,
        bloodSugarDiff: 1,
        step: 1,
        stepLastDT: 1,
        stepDiff: 1,
        spo2: 1,
        spo2LastDT: 1,
        spo2Diff: 1,
        pef: 1,
        pefLastDT: 1,
        pefDiff: 1,
        cpts: 1,
        activeMemos: 1,
        measureFreq: 1,
        warningLog: 1,
        numUnreadMessages: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
        dataReceivedDT: 1,
        diagnosisCodes: 1,
        practiceId: 1,
        missedCall: 1,
        devices: 1,
        kardiaId: 1,
        timeSpent: 1,
        optInSMS: 1,
        email: 1,
        events: 1,
        liveBilling: 1,
        services: 1,
        careplans: 1,
        memberTitle: 1,
      };

      const countDoc = await fetchMembersWithOptions({
        query,
        projection: { _id: 1 },
        limit: 1,
      });
      const total = countDoc.nTot;

      const memberDataDoc = await fetchMembersWithOptions({
        query,
        projection,
        sortOption,
        limit: total,
        skip: 0,
      });

      let memberData = memberDataDoc.data;

      for (let i = 0; i < memberData.length; i++) {
        memberData[i] = setCurrBillingInsights(
          memberData[i],
          userCustomData.timeZone,
        );
      }

      setMemberAll(memberData);
      setNTot(total);
      return memberData;
    };

    const loadMembersCC = async () => {
      // CC: Care Calendar
      const projection = {
        _id: 1,
        owner_id: 1,
        disenrolled: 1,
        name: 1,
        phone: 1,
        dob: 1,
        gender: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
        // enrollmentDate: 1,
        events: 1,
      };

      const options = { projection };

      let memberResults = await fetchMembersWithOptions(options);

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      setMemberAll(
        memberData.filter((member) => !member.disenrolled),
      );

      return memberData;
    };

    const fetchMembersByQuery = async (query, projection, sort) => {
      const options = { query, projection, sort };

      let memberResults = await fetchMembersWithOptions(options);

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      return memberData;
    };

    const loadMembersDB = async () => {
      const projection = {
        _id: 1,
        owner_id: 1,
        disenrolled: 1,
        name: 1,
        phone: 1,
        dob: 1,
        gender: 1,
        enrollmentDate: 1,
        devices: 1,
        address: 1,
        bodyWeight: 1,
        bodyWeightLastDT: 1,
        bodyWeightDiff: 1,
        bodyWeightData: 1,
        systolicBP: 1,
        systolicBPLastDT: 1,
        systolicBPDiff: 1,
        diastolicBP: 1,
        diastolicBPDiff: 1,
        systolicBPData: 1,
        pulse: 1,
        pulseDiff: 1,
        pulseLastDT: 1,
        ecg: 1,
        ecgLastDT: 1,
        ecgData: 1,
        bloodSugar: 1,
        bloodSugarLastDT: 1,
        bloodSugarDiff: 1,
        bloodSugarData: 1,
        lastActiveDate: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
      };

      const options = { projection };

      let memberResults = await fetchMembersWithOptions(options);

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      setMemberAll(
        memberData.filter((member) => !member.disenrolled),
      );

      return memberData;
    };

    const loadMembersBasic = async () => {
      const projection = {
        _id: 1,
        dob: 1,
        name: 1,
      };

      const options = { projection };

      let memberResults = await fetchMembersWithOptions(options);

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      setMemberAll(
        memberData.filter((member) => !member.disenrolled),
      );

      return memberData;
    };

    // this function is called by billing reports.
    const loadMembersForBillingReports = async (ymRef = getYM()) => {
      const [year, month] = ymRef.split('-').map(Number);
      const firstDayOfMonth = new Date(year, month - 1, 1).toISOString();
      const query = {
        $or: [
          { disenrolled: false },
          {
            $and: [
              { disenrolled: true },
              {
                disenrollHist: {
                  $elemMatch: { changedAt: { $gte: firstDayOfMonth } },
                },
              },
            ],
          },
          // CCM-enrolled members (including RPM-disenrolled who remain on CCM)
          {
            services: platformServices.CCM,
            disenrolledCcm: { $ne: true },
          },
          {
            $and: [
              { services: platformServices.CCM },
              { disenrolledCcm: true },
              {
                disenrollCcmHist: {
                  $elemMatch: { changedAt: { $gte: firstDayOfMonth } },
                },
              },
            ],
          },
        ],
      };

      const projection = {
        _id: 1,
        disenrolled: 1,
        'disenrollHist.changedAt': 1,
        disenrolledCcm: 1,
        'disenrollCcmHist.changedAt': 1,
        name: 1,
        dob: 1,
        gender: 1,
        enrollmentDate: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
        'diagnosisCodes.code': 1,
        practiceId: 1,
        mrn: 1,
        owner_id: 1,
        services: 1,
        enrollmentDateCcm: 1,
        'careplans.assessmentDate': 1,
        'chronicConditions.code': 1,
        'bodyWeightData.time': 1,
        'bloodSugarData.time': 1,
        'systolicBPData.time': 1,
        'systolicBPData.value': 1,
        'diastolicBPData.time': 1,
        'diastolicBPData.value': 1,
        'ecgData.time': 1,                           
      };

      const showLiveBilling = userCustomData.showLiveBilling || false;
      projection[`liveBilling.${ymRef}.billingInsights`] = 1;
      projection[`liveBilling.${ymRef}.ccmBillingInsights`] = 1;
      projection[`liveBilling.${ymRef}.time99453Changed`] = 1;
      projection[`liveBilling.${ymRef}.time99454Changed`] = 1;
      projection[`liveBilling.${ymRef}.time99457Changed`] = 1;
      projection[`liveBilling.${ymRef}.time99458Changed`] = 1;
      projection[`liveBilling.${ymRef}.time99490Changed`] = 1;
      projection[`liveBilling.${ymRef}.time99439Changed`] = 1;
      // use pastBillingReports for months after 2021-09. Not sure if we have past billing reports before this date.
      if (
        !showLiveBilling && // for orgs where showLiveBilling is true, do not fetch past reports, we want to use live only for these orgs.
        new Date(ymRef) >= new Date('2021-09')
      ) {
        projection[`pastBillingReports.${ymRef}.billingInsights`] = 1;
        projection[
          `pastBillingReports.${ymRef}.ccmBillingInsights`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99453Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99454Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99457Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99458Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99490Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99439Changed`
        ] = 1;
      }

      const options = { query, projection };

      const memberResults = await fetchMembersForBillingReports({
        ymRef,
        ...options,
      });

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      setMemberAll(memberData);
      return memberData;
    };

    const loadMembersFull = async (ymRef = getYM()) => {
      const projection = {
        _id: 1,
        disenrolled: 1,
        disenrollHist: 1,
        name: 1,
        dob: 1,
        gender: 1,
        enrollmentDate: 1,
        assignedPhysician: 1,
        assignedCareManager: 1,
        diagnosisCodes: 1,
        practiceId: 1,
        mrn: 1,
        owner_id: 1,
        services: 1,
        enrollmentDateCcm: 1,
        careplans: 1,
        chronicConditions: 1,
        bodyWeightData: 1,
        bloodSugarData: 1,
        systolicBPData: 1,
        diastolicBPData: 1,
        ecgData: 1,
      };

      const showLiveBilling = userCustomData.showLiveBilling || false;
      projection[`liveBilling.${ymRef}`] = 1;
      // use pastBillingReports for months after 2021-09. Not sure if we have past billing reports before this date.
      if (
        !showLiveBilling && // for orgs where showLiveBilling is true, do not fetch past reports, we want to use live only for these orgs.
        new Date(ymRef) >= new Date('2021-09')
      ) {
        projection[`pastBillingReports.${ymRef}.billingInsights`] = 1;
        projection[
          `pastBillingReports.${ymRef}.ccmBillingInsights`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99453Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99454Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99457Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99458Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99490Changed`
        ] = 1;
        projection[
          `pastBillingReports.${ymRef}.time99439Changed`
        ] = 1;
      }

      const options = { projection };

      const memberResults = await fetchMembersWithOptions(options);

      const memberData = memberResults.data;

      _.forEach(memberData, function (d) {
        d._id = ObjectID(d._id).toHexString();
      });

      setMemberAll(memberData);
      return memberData;
    };

    const updateNote = async (
      member,
      noteValue,
      time,
      isMemo,
      source = 'members',
    ) => {
      // NOTE: affects billing
      const parsedNoteValue = parseNote(noteValue);
      const noteItem = {
        time: time,
        by: userCustomData.namePublic,
        email: userCustomData.email,
        value: isMemo
          ? parsedNoteValue.replace('memo:', '')
          : parsedNoteValue,
        noteType: isMemo ? 'memo' : 'note',
        noteID: member.noteData.length + 1,
        clearedAt: '',
        clearedBy: '',
        isActive: true,
      };

      member = {
        ...member,
        noteData: [...member.noteData, noteItem],
      };
      member.activeMemos = activeMemos(member.noteData);

      await updateMember(member._id, {
        $push: { noteData: noteItem },
        $set: { activeMemos: member.activeMemos },
      });

      setMemberOne(member);
    };

    const updateAlert = async (
      member,
      noteValue,
      alertId,
      toContact,
      toName,
      url,
    ) => {
      // NOTE: affects billing
      const noteItem = {
        time: new Date().toISOString(),
        isDirectSecureMsg: false,
        by: userCustomData.namePublic,
        email: userCustomData.email,
        value: parseNote(noteValue),
        alertId: alertId,
        clearedAt: '',
        to: {
          contactInfo: toContact,
          name: toName,
        },
        url: url,
        status: 'open',
      };
      await updateMember(member._id, {
        $push: { doctorAlerts: noteItem },
      });
      setMemberOne({
        ...member,
        doctorAlerts: [...(member.doctorAlerts || []), noteItem],
      });
    };

    const clearMemo = async (member, noteItem) => {
      const updatedNoteData = member.noteData.map((note) =>
        note.noteID === noteItem.noteID
          ? {
              ...note,
              isActive: false,
              clearedAt: new Date().toISOString(),
              clearedBy: userCustomData.email,
            }
          : note,
      );

      const updatedMember = {
        ...member,
        noteData: updatedNoteData,
        activeMemos: activeMemos(updatedNoteData),
      };

      await updateMember(
        member._id,
        {
          $set: {
            'noteData.$[elem].isActive': false,
            'noteData.$[elem].clearedAt': new Date().toISOString(),
            'noteData.$[elem].clearedBy': userCustomData.email,
            activeMemos: updatedMember.activeMemos,
          },
        },
        [{ 'elem.noteID': noteItem.noteID }],
      );

      setMemberOne(updatedMember);
    };

    const handleDeleteNotes = async (
      member,
      noteItem,
      source = 'members',
    ) => {
      const updatedNoteData = member.noteData.map((note) =>
        note.noteID === noteItem.noteID
          ? {
              ...note,
              isDeleted: true,
              deletedAt: new Date().toISOString(),
              deletedBy: userCustomData.email,
              ...(note.noteType === 'memo' && {
                isActive: false,
                clearedAt: new Date().toISOString(),
                clearedBy: userCustomData.email,
              }),
            }
          : note,
      );

      const updatedMember = {
        ...member,
        noteData: updatedNoteData,
        activeMemos: activeMemos(updatedNoteData),
      };

      const updateOps = {
        $set: {
          'noteData.$[elem].isDeleted': true,
          'noteData.$[elem].deletedAt': new Date().toISOString(),
          'noteData.$[elem].deletedBy': userCustomData.email,
          activeMemos: updatedMember.activeMemos,
        },
      };

      if (noteItem.noteType === 'memo') {
        updateOps.$set['noteData.$[elem].isActive'] = false;
        updateOps.$set['noteData.$[elem].clearedAt'] =
          new Date().toISOString();
        updateOps.$set['noteData.$[elem].clearedBy'] =
          userCustomData.email;
      }

      await updateMember(member._id, updateOps, [
        { 'elem.noteID': noteItem.noteID },
      ]);

      setMemberOne(updatedMember);
    };

    const updateMessage = async (member, messageValue, category) => {
      const msgItem = {
        time: new Date().toISOString(),
        by: userCustomData.namePublic,
        email: userCustomData.email,
        senderType: senderTypes.USER,
        value: messageValue,
        isRead: true,
      };
      if (category) msgItem.templateCategory = category;

      const memberNew = await updateMember(member._id, {
        $push: { messageData: msgItem },
      });

      setMemberOne(memberNew);
      sendSMS(member.phone, messageValue);
    };

    const updateMessageRead = async (
      memberId,
      source = 'members',
    ) => {
      const memberNew = await updateMember(memberId, {
        'messageData.$[].isRead': true,
        numUnreadMessages: 0,
      });

      setMemberOne(memberNew);
    };

    const updateReviewStatus = async (memberId) => {
      await updateMember(memberId, { needsReview: false });
      return { ...memberOne, needsReview: false };
    };

    const updateTimeSpent = async (
      memberId,
      timeSpent,
      ymRef = getYM(),
    ) => {
      await updateMember(memberId, {
        ['timeSpent.' + ymRef]: {
          value: timeSpent,
          time: new Date().toISOString(),
        },
      });
    };

    const updateMedications = async (member, medications) => {
      member.medications = activeMeds(medications);
      const memberNew = await updateMember(member._id, {
        medications: member.medications,
      });
      setMemberOne(memberNew);
    };

    const updateDiagnoses = async (member, diagnoses) => {
      const memberNew = await updateMember(member._id, {
        diagnosisCodes: diagnoses,
      });
      setMemberOne(memberNew);
    };

    const createCareplan = async (member, careplans) => {
      const memberNew = await updateMember(member._id, {
        careplans: careplans,
      });
      setMemberOne(memberNew);
    };

    const updateCarePlan = async (member, updatedCarePlan) => {
      const updatedCareplans = member.careplans.map((plan) =>
        plan.assessmentDate === updatedCarePlan.assessmentDate
          ? updatedCarePlan
          : plan,
      );
      const memberNew = await updateMember(member._id, {
        careplans: updatedCareplans,
      });
      setMemberOne(memberNew);
    };

    const addChronicConditions = async (member, conditions) => {
      const memberNew = await updateMember(member._id, {
        chronicConditions: conditions,
      });
      setMemberOne(memberNew);
    };

    const addMemberAllergies = async (member, allergies) => {
      const memberNew = await updateMember(member._id, {
        allergies: allergies,
      });
      setMemberOne(memberNew);
    };

    const addCarePlanProgress = async (
      member,
      currentPlan,
      newProgressUpdate,
    ) => {
      const updatedCareplans = member.careplans.map((plan) => {
        if (plan.assessmentDate === currentPlan.assessmentDate) {
          return {
            ...plan,
            progressUpdates: [
              ...(plan.progressUpdates || []),
              newProgressUpdate,
            ],
          };
        }
        return plan;
      });
      const memberNew = await updateMember(member._id, {
        careplans: updatedCareplans,
      });
      setMemberOne(memberNew);
    };

    const updateCareCalendar = async (
      member,
      careCalendar,
      nextC2Checkin,
    ) => {
      await updateMember(member._id, {
        careCalendar: careCalendar,
        nextC2Checkin: nextC2Checkin,
      });
      loadMember(member._id);
    };

    const enrollMember = async (member) => {
      // If new, add the member to the "members" collection
      const createdMember = await createMember(member);
      const insertedId = createdMember._id;
      member._id = insertedId;

      // Get derived fields from the backend
      const derivedFields = await selectDerivedFields(insertedId);

      // Update member in db with processed derived fields
      await updateMember(insertedId, derivedFields);
      if (member.messageData.length > 0) {
        // Send the enrollment message to the member
        sendSMS(member.phone, member.messageData[0].value);
      }
      // Order or connect BodyTrace devices (BT004, BT105, SMIG)
      for (let i = 0; i < member.devices.length; i++) {
        const device = member.devices[i];

        if (member.additionalInfo[device] === 'request') {
          // Order new device - uses orderBodyTraceDevice endpoint
          await orderBodyTraceDevice(insertedId.toString(), device);
        } else if (member.additionalInfo[device] === 'connect') {
          // Connect existing device with IMEI - uses connectExistingDevice endpoint
          const imei = member.additionalInfo[device + 'Imei'];
          await connectExistingDevice(
            insertedId.toString(),
            device,
            imei,
          );
        }
      }

      for (let i = 0; i < member.tenoviDevices.length; i++) {
        const tenoviDevice = member.tenoviDevices[i];
        if (member.additionalInfo[tenoviDevice].state === 'request') {
          await orderTenoviDevice(
            insertedId.toString(),
            tenoviDevice,
          ); // TODO: Optimize to only update members tenoviDevices once all have been ordered
          await updateMember(insertedId, {
            $push: { tenoviDevices: tenoviDevice },
          });
        } else if (
          member.additionalInfo[tenoviDevice].state === 'connect'
        ) {
          try {
            await connectTenoviDevice(
              insertedId.toString(),
              tenoviDevice,
              member.additionalInfo[tenoviDevice].gatewayId,
            );
          } catch (error) {
            const apiMessage =
              error?.message ||
              'Unknown error';

            const gatewayId = member.additionalInfo[tenoviDevice].gatewayId;
            const wrappedError = new Error(
              `Failed to connect Tenovi device (${tenoviDevice})${
                gatewayId ? ` with gatewayId ${gatewayId}` : ''
              }. ${apiMessage}`,
            );
            wrappedError.memberId = insertedId.toString();
            wrappedError.tenoviDevice = tenoviDevice;
            wrappedError.gatewayId = gatewayId;
            wrappedError.deviceConnectionFailed = true;
            wrappedError.enrollmentPartialSuccess = true;
            throw wrappedError;
          }
          await updateMember(insertedId, {
            $push: { tenoviDevices: tenoviDevice },
          });
        }
      }
    };

    const addAdditionalCcmService = async (member, ccmProfile) => {
      try {
        const memberNew = await updateMember(member._id, {
          $set: {
            caregiver: {
              name: ccmProfile.caregiverName,
              phone: ccmProfile.caregiverPhone,
            },
            methodOfCommunication: ccmProfile.methodOfCommunication,
            ccmPatientConsent: true,
            disenrolledCcm: false,
            enrollmentDateCcm: new Date().toISOString(),
            personToContact: ccmProfile.personToContact,
          },
          $push: {
            services: 'CCM',
            disenrollCcmHist: {
              enrollmentStatus: 'enrolled',
              changedAt: new Date().toISOString(),
              changedBy: userCustomData.email,
            },
          },
        });

        setMemberOne(memberNew);
      } catch (error) {
        throw error; // Re-throw so the modal can handle it
      }
    };

    const updateDataExclusion = async (member, measure, time) => {
      // NOTE: This affects the billing insights
      const measureData = member[measure].map((measurement) => {
        if (measurement.time === time) {
          measurement.exclude = !measurement.exclude;
        }
        return measurement;
      });

      // Update the database with the modified measure data
      await updateMember(member._id, {
        [measure]: measureData,
      });

      // Process readings from the updated database data
      const processedMember = await processReadings(member._id);
      setMemberOne(processedMember);
    };

    const updateMeasurements = async (member, doc) => {
      // NOTE: This process usually happens on the backend when a new measurement arrives.
      // When manually entering measurements, the process should be mimicked as follows:

      // First, update the member with the new measurements
      await updateMember(member._id, doc);

      // Apply clinical rules to get risk scores
      const measureTypes = Object.keys(doc.$push || {});
      const ruleOutput = await applyClinicalRules(
        member._id,
        measureTypes,
      );

      // Update with clinical risk scores
      if (ruleOutput?.clinicalRiskScores) {
        await updateMember(member._id, {
          clinicalRiskScores: ruleOutput.clinicalRiskScores,
        });
      }
      // Reload the processed member data
      const processedMember = await processReadings(member._id);
      setMemberOne(processedMember);
    };

    const handleRiskSuggestion = async (member) => {
      const memberNew = await updateMember(member._id, {
        valueInsights: member.valueInsights,
      });
      setMemberOne(await processMemberData(memberNew, undefined));
    };

    const assignProvider = async (
      member,
      providerId,
      providerType,
    ) => {
      const memberNew = await updateMember(member._id, {
        [providerType]: providerId,
      });
      setMemberOne(await processMemberData(memberNew, undefined));
    };

    const clearAlert = async (member, alertTime) => {
      const warningLogNew = member.warningLog.map((x) => {
        x.needsReview = false;
        return x;
      });
      await updateMember(member._id, {
        warningLog: warningLogNew,
        needsReview: false,
      });
    };

    const addEmDate = async (member, date) => {
      await updateMember(member._id, {
        $addToSet: { emDates: date },
      });
    };

    const removeEmDate = async (member, date) => {
      await updateMember(member._id, {
        $pull: { emDates: date },
      });
    };

    const addEvent = async (member, event) => {
      const numDaysTillEvent = daysTillEvent([
        ...(member.events || []),
        event,
      ]);

      await updateMember(member._id, {
        $push: { events: event },
        $set: { days_till_event: numDaysTillEvent },
      });

      await loadMember(member._id);
    };

    const editEvent = async (member, newEvent, i) => {
      const setObject = {};

      let eventsTmp = member.events;
      eventsTmp[i] = newEvent;
      const numDaysTillEvent = daysTillEvent(eventsTmp);

      setObject[`events.${i}`] = newEvent;
      setObject['days_till_event'] = numDaysTillEvent;

      await updateMember(member._id, setObject);

      await loadMember(member._id);
    };

    const deleteEvent = async (member, i) => {
      const setObject = {};

      let eventsTmp = member.events.filter((event, j) => i !== j);
      const numDaysTillEvent = daysTillEvent(eventsTmp);
      setObject['events'] = eventsTmp;
      setObject['days_till_event'] = numDaysTillEvent;

      await updateMember(member._id, setObject);

      await loadMember(member._id);
    };

    const value = {
      memberAll,
      nTot,
      nLimit,
      memberOne,
      opInsights,
      actions: {
        reset,
        loadMember,
        loadMembers,
        loadMembersForServiceTabs,
        loadMembersDB,
        loadMembersCC,
        loadMembersBasic,
        loadMembersFull,
        loadMembersForBillingReports,
        fetchMembersByQuery,
        updateNote,
        updateAlert,
        clearMemo,
        handleDeleteNotes,
        updateMessage,
        updateMessageRead,
        updateTimeSpent,
        updateMedications,
        updateDiagnoses,
        createCareplan,
        updateCarePlan,
        addChronicConditions,
        addMemberAllergies,
        addCarePlanProgress,
        updateCareCalendar,
        enrollMember,
        addAdditionalCcmService,
        updateDataExclusion,
        updateMeasurements,
        updateReviewStatus,
        handleRiskSuggestion,
        assignProvider,
        clearAlert,
        addEmDate,
        removeEmDate,
        addEvent,
        editEvent,
        deleteEvent,
      },
    };
    return value;
  }, [memberAll, nTot, memberOne, opInsights, userCustomData]);
  return (
    <StitchMembersContext.Provider value={memberInfo}>
      {props.children}
    </StitchMembersContext.Provider>
  );
}

StitchMembersProvider.propTypes = {
  children: PropTypes.element,
};
